
import { InputBaseComponentProps, TextField } from '@mui/material';
import React, { memo, useState } from 'react';
import { GMIconButton } from '../../../Buttons/IconButton';
import { GMInputWrapper } from '../../InputWrapper';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { useLocalization } from '../../../../Contexts/Localization';
import { GMOutlinedInputLabel } from '../../InputLabel';
import css from './PasswordField.module.scss';
import { ErrorLevel } from '../../../../API/Enums';
import { GMPasswordFieldProps } from '..';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { NumericMask } from './NumericMask/NumericMask';

const PasswordField: GoMotoComponent<GMPasswordFieldProps> = memo(({
    name,
    value,
    autofocus,
    onChange,
    onBlur: propsOnBlur,
    onFocus: propsOnFocus,
    maxLength = 255,
    noMaxLength,
    helperText,
    label,
    placeholder,
    shrinkLabel,
    autoComplete = 'on',
    passwordType = 'password',
    numericOnly,
    unmaskOnFocus,
    maskOnBlur,
    showTooltipText,
    hideTooltipText,
    ...wrapperProps
}) => {

    const [isHidden, setIsHidden] = useState<boolean>(true);
    const Localization = useLocalization();

    const inputProps: InputBaseComponentProps = {
        maxLength: noMaxLength ? undefined : maxLength,
        size: 1,
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (unmaskOnFocus) {
            setIsHidden(false);
        }

        if (propsOnFocus) {
            propsOnFocus(e);
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (maskOnBlur) {
            setIsHidden(true);
        }

        if (propsOnBlur) {
            propsOnBlur(e);
        }
    };

    return (
        <GMInputWrapper {...wrapperProps}>
            {
                wrapperProps.variant === 'outlined' ?
                    <GMOutlinedInputLabel
                        label={label}
                        required={wrapperProps.required}
                        error={wrapperProps.error}
                        disabled={wrapperProps.disabled}
                        color={wrapperProps.color}
                    /> : null
            }
            <TextField
                className={isHidden && value ? css.maskedInput : undefined}
                autoComplete={autoComplete}
                variant={wrapperProps.variant}
                type={isHidden && passwordType === 'password' ? 'password' : 'text'}
                name={name}
                label={wrapperProps.variant === 'standard' ? label : undefined}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                disabled={wrapperProps.disabled}
                autoFocus={autofocus}
                inputProps={inputProps}
                helperText={helperText}
                InputLabelProps={
                    shrinkLabel ? { shrink: true } :
                        undefined
                }
                InputProps={{
                    endAdornment: (
                        <GMIconButton
                            color={wrapperProps.color}
                            tooltip={isHidden ? showTooltipText ?? Localization.ShowPassword : hideTooltipText ?? Localization.HidePassword}
                            notTabbable
                            onClick={() => setIsHidden(!isHidden)}
                        >
                            {isHidden ? <BsEye /> : <BsEyeSlash />}
                        </GMIconButton>
                    ),
                    inputComponent: numericOnly ? NumericMask : undefined,
                }}
                placeholder={placeholder}
                error={wrapperProps.error !== undefined && wrapperProps.error !== ErrorLevel.None}
                required={wrapperProps.required}
            />
        </GMInputWrapper >
    );
});

export default PasswordField;
